import * as React from 'react';
import { Fabric, PrimaryButton } from '@fluentui/react';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { viewStyles } from 'styles/viewStyles';
import { formStyles } from 'styles/formStyles';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useMaschinenkundeList } from 'api/maschinenkunde';
import { MaschinenkundeEdit } from 'components/Kunde/Maschinenkunde/MaschinenkundeEdit';
import { MaschinenkundeItem } from 'components/Kunde/Maschinenkunde/MaschinenkundeItem';
import { useParams } from 'react-router-dom';
import { RecordViewUrlParams } from 'layout/App/types';
import { Helmet } from 'react-helmet';
import { Checkbox } from '@fluentui/react/lib/Checkbox';

const styles = mergeStyleSets({
    searchBar: {
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr 200px',
        gridColumnGap: 10,
        marginTop: 10,
    },
});

interface MaschinenkundeViewProps {
    kNr?: number;
}

interface MaschinenkundeEditData {
    kNr: number;
    lNr: number;
}

export const MaschinenkundeView: React.FC<MaschinenkundeViewProps> = ({ kNr }) => {
    const [maschinenkundeEditData, setMaschinenkundeEditData] = React.useState<MaschinenkundeEditData | null>(null);
    const { urlKundeNr } = useParams<RecordViewUrlParams>();

    const [showArchiv, setShowArchiv] = React.useState(false);

    const maschinenkundeList = useMaschinenkundeList({
        kNr,
        includeArchiv: showArchiv,
        maxRows: 250,
    });

    return (
        <Fabric className={viewStyles.subItemContainer}>
            <Helmet>
                <title>
                    {urlKundeNr ? 'Kunde ' + urlKundeNr + ' | ' : ''}
                    Maschinenkunde
                </title>
            </Helmet>
            {maschinenkundeList.isLoading || maschinenkundeList.isFetching ? (
                <LoadingIndicator />
            ) : (
                <>
                    <div className={styles.searchBar}>
                        <div>
                            <Checkbox
                                className={formStyles.checkboxInsideForm}
                                label="Archivierte Maschinen anzeigen"
                                checked={showArchiv}
                                onChange={(event, checked) => {
                                    setShowArchiv(checked || false);
                                }}
                            />
                        </div>
                        <div />
                        {kNr && (
                            <PrimaryButton
                                onClick={() =>
                                    setMaschinenkundeEditData({
                                        kNr,
                                        lNr: -1,
                                    })
                                }
                                text="Neue Maschine"
                            />
                        )}
                    </div>
                    <div className={viewStyles.listContainer}>
                        {maschinenkundeList?.data && maschinenkundeList?.data.length === 0 ? (
                            <div className={formStyles.noData}>Keine Maschinenkunde.</div>
                        ) : (
                            maschinenkundeList?.data?.map((maschinenkunde) => (
                                <MaschinenkundeItem
                                    key={maschinenkunde.lNr}
                                    maschinenkunde={maschinenkunde}
                                    editMaschinenkunde={() =>
                                        setMaschinenkundeEditData({
                                            kNr: maschinenkunde.kNr,
                                            lNr: maschinenkunde.lNr || 0,
                                        })
                                    }
                                />
                            ))
                        )}
                    </div>
                </>
            )}
            {maschinenkundeEditData !== null && (
                <MaschinenkundeEdit
                    kNr={maschinenkundeEditData.kNr}
                    lNr={maschinenkundeEditData.lNr}
                    onClose={(refetch) => {
                        setMaschinenkundeEditData(null);
                        if (refetch) {
                            maschinenkundeList.refetch().then();
                        }
                    }}
                />
            )}
        </Fabric>
    );
};
